import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AndroWebCoreComponent } from '@app/core/AndroWebCoreComponent';
import { NavigatorService } from '@app/core/navigator.service';
import { DisplayOccasionType } from '@app/models/display-occasion-type';
import { NavigationSectionType } from '@app/models/navigation/navigation-section-type';
import { OrderOccasion } from '@app/models/order-occasion';
import { PageSEO } from '@app/models/page-seo';
import { MenuService } from '@app/api/menu.service';
import { SEOService } from '@app/shared/services/seo.service';
import { takeUntil } from 'rxjs';
import { Deal } from '@app/models/menu/deal';
import { Menu } from '@app/models/menu/menu';

@Component({
  template: `
  <ng-template #ProductModal>
    <app-deals [deal]="deal" [occasion]="orderOccasion" [group]="group" (close)="close()"></app-deals>
  </ng-template>
  `
})
export class DealsDialogEntryComponent extends AndroWebCoreComponent implements AfterViewInit, OnDestroy {
  @ViewChild('ProductModal') private _productModal: TemplateRef<any>;

  public deal: Deal;
  public orderOccasion: OrderOccasion;
  public group: string;

  private _dialogRef: MatDialogRef<any, any>;
  private _seoSet: boolean;

  constructor(
    private _router: Router,
    private _seoService: SEOService,
    private _menuService: MenuService,
    private _activatedRoute: ActivatedRoute,
    private _navigatorService: NavigatorService
  ) {
    super();
  }

  ngAfterViewInit(): void {
    this.updatePageSEO(true);

    this._menuService.menu$
        .pipe(takeUntil(this.destroy$))
        .subscribe((menu: Menu) => {
          if (!menu) {
            return;
          }

          // const location = this._activatedRoute.snapshot.paramMap.get('location');
          const routeOccasion: string = this._navigatorService.changeTakeAwayToCollection(this._activatedRoute.snapshot.paramMap.get('occasion'));
          this.orderOccasion = DisplayOccasionType[this._navigatorService.toTitleCase(routeOccasion)];
          this.group = this._activatedRoute.snapshot.paramMap.get('displayGroup');
          this.deal = menu.Deals.find((x: Deal) => x.Id === this._activatedRoute.snapshot.paramMap.get('dealId'));
          this.updatePageSEO(false);
          this.openDealsModal();
        });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this._seoSet) {
      this._seoService.restore();
    }
  }

  /**
   * closes the dialog.
   */
  public close(): void {
    this._dialogRef.close();
  }

  /**
  * Takes a string with a well defined placeholder ( '{{dealName}}' ) and replaces the placeholder with the deals name then returns the updated value
  * @param useDefault - determines whether the default meta title and description text should be used or not
  */
  private updatePageSEO(useDefault: boolean): void {
    if (!useDefault) {
      const dealsPageSEO = this.tenant.PageSeos.find((seo: PageSEO) => seo.PageName.toLowerCase() === 'deals');

      if (dealsPageSEO) {
        this._seoSet = true;
        this._seoService.updateTitle(this.replaceSEOStrings(dealsPageSEO.Title));
        this._seoService.updateDescription(this.replaceSEOStrings(dealsPageSEO.Description));
        return;
      }
    }

    const title: string = this.replaceSEOStrings('{{dealName}} - {{TenantWebsiteName}}');
    this._seoService.updateTitle(title);
    this._seoService.updateDescription('{{TenantWebsiteName}} fresh food prepared daily');
    this._seoSet = true;
  }

  /**
  * takes a string with a well defined placeholder ( '{{dealName}}' ) and replaces the placeholder with the deals name then returns the updated value
  * @param updateText - the text string with a well defined placeholder
  */
  private replaceSEOStrings(updateText: string): string {
    let result = '';

    if (this.deal?.Name && updateText.includes('{{dealName}}')) {
      result = result
        ? result.replace('{{dealName}}', this.deal.Name)
        : updateText.replace('{{dealName}}', this.deal.Name);
    }

    return result ?? updateText;
  }

  /**
   * opens the product component in a dialog.
   */
  private openDealsModal(): void {
    const config: MatDialogConfig<any> = {
      height: this.isMobile ? '100%' : '',
      panelClass: ['custom-dialog-two-container', 'product-modal'],
      width: this.isMobile ? '100%' : '450px',
      ariaModal: false
    };

    this._dialogRef = this.openDialog(this._productModal, 'productsModal', config);

    this._dialogRef.afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          const params: Params = this._activatedRoute.snapshot.params;

          if (params && (params.productId || params.productName)) {
            const routePath: string = this._navigatorService.generateRoutePath(params.location, params.occasion, NavigationSectionType.Menu, params.displayGroup);
            this._router.navigate([routePath]);
          }
        });
  }
}
